/**
 * useDragControls - Reusable mouse drag and wheel handling with optional momentum
 *
 * Provides drag deltas and wheel deltas to callbacks.
 * Attaches mousemove/mouseup to window so dragging works outside the element.
 * When momentum is enabled, continues applying velocity after release with decay.
 */

import { useEffect, useRef, RefObject } from 'react';

export interface ModifierKeys {
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
  meta: boolean;
}

interface DragControlsOptions {
  /** Called with (dx, dy) pixel deltas during drag (and momentum if enabled) */
  onDrag?: (dx: number, dy: number, modifiers: ModifierKeys) => void;
  /** Called with wheel deltaY and mouse position relative to element */
  onWheel?: (delta: number, mouseX: number, mouseY: number) => void;
  /** Called when drag starts */
  onDragStart?: () => void;
  /** Called when drag ends (before momentum starts) */
  onDragEnd?: () => void;
  /** Enable momentum after release (default: false) */
  momentum?: boolean;
  /** Friction coefficient 0-1, higher = more friction (default: 0.05) */
  friction?: number;
  /** Minimum velocity to continue momentum (default: 0.1) */
  minVelocity?: number;
}

/**
 * Hook to handle mouse drag and wheel interactions on an element.
 * Returns a ref to attach to the target element.
 */
export function useDragControls<T extends HTMLElement>(
  options: DragControlsOptions
): RefObject<T> {
  const elementRef = useRef<T>(null);
  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const lastTimeRef = useRef(0);
  const velocityRef = useRef({ x: 0, y: 0 });
  const modifiersRef = useRef<ModifierKeys>({ shift: false, ctrl: false, alt: false, meta: false });
  const momentumIdRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Store callbacks in refs to avoid stale closures
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    let rafId: number | null = null;
    let attached = false;

    const stopMomentum = () => {
      if (momentumIdRef.current !== null) {
        cancelAnimationFrame(momentumIdRef.current);
        momentumIdRef.current = null;
      }
    };

    const runMomentum = () => {
      const opts = optionsRef.current;
      const friction = opts.friction ?? 0.05;
      const minVelocity = opts.minVelocity ?? 0.1;

      velocityRef.current.x *= (1 - friction);
      velocityRef.current.y *= (1 - friction);

      const speed = Math.sqrt(
        velocityRef.current.x * velocityRef.current.x +
        velocityRef.current.y * velocityRef.current.y
      );

      if (speed < minVelocity) {
        stopMomentum();
        return;
      }

      opts.onDrag?.(velocityRef.current.x, velocityRef.current.y, modifiersRef.current);
      momentumIdRef.current = requestAnimationFrame(runMomentum);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      const now = performance.now();
      const dt = now - lastTimeRef.current;
      const dx = e.clientX - lastMouseRef.current.x;
      const dy = e.clientY - lastMouseRef.current.y;

      // Update modifier keys
      modifiersRef.current = {
        shift: e.shiftKey,
        ctrl: e.ctrlKey,
        alt: e.altKey,
        meta: e.metaKey
      };

      if (dt > 0) {
        // If there's been a pause (> 100ms), decay velocity based on time elapsed
        if (dt > 100) {
          const decayFactor = Math.exp(-dt / 50); // Exponential decay
          velocityRef.current.x *= decayFactor;
          velocityRef.current.y *= decayFactor;
        }

        // Blend new movement into velocity
        const alpha = 0.3;
        velocityRef.current.x = alpha * dx + (1 - alpha) * velocityRef.current.x;
        velocityRef.current.y = alpha * dy + (1 - alpha) * velocityRef.current.y;
      }

      lastMouseRef.current = { x: e.clientX, y: e.clientY };
      lastTimeRef.current = now;

      optionsRef.current.onDrag?.(dx, dy, modifiersRef.current);
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        optionsRef.current.onDragEnd?.();

        if (optionsRef.current.momentum) {
          // Check if user paused before releasing - if so, kill momentum
          const timeSinceLastMove = performance.now() - lastTimeRef.current;
          if (timeSinceLastMove > 100) {
            // User stopped moving before releasing, decay velocity
            const decayFactor = Math.exp(-timeSinceLastMove / 50);
            velocityRef.current.x *= decayFactor;
            velocityRef.current.y *= decayFactor;
          }

          const speed = Math.sqrt(
            velocityRef.current.x * velocityRef.current.x +
            velocityRef.current.y * velocityRef.current.y
          );
          if (speed > (optionsRef.current.minVelocity ?? 0.1)) {
            runMomentum();
          }
        }
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    const handleMouseDown = (e: MouseEvent) => {
      stopMomentum();
      velocityRef.current = { x: 0, y: 0 };

      isDraggingRef.current = true;
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
      lastTimeRef.current = performance.now();
      e.preventDefault();
      e.stopPropagation();
      optionsRef.current.onDragStart?.();
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    };

    const handleWheel = (e: WheelEvent) => {
      if (optionsRef.current.onWheel && elementRef.current) {
        e.preventDefault();
        e.stopPropagation();
        const rect = elementRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        optionsRef.current.onWheel(e.deltaY, mouseX, mouseY);
      }
    };

    const attachListeners = (element: T) => {
      element.addEventListener('mousedown', handleMouseDown);
      element.addEventListener('wheel', handleWheel, { passive: false });

      cleanupRef.current = () => {
        stopMomentum();
        element.removeEventListener('mousedown', handleMouseDown);
        element.removeEventListener('wheel', handleWheel);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    };

    const tryAttach = () => {
      const element = elementRef.current;
      if (!element) {
        // Element not ready yet, try again next frame
        rafId = requestAnimationFrame(tryAttach);
        return;
      }
      if (attached) return;
      attached = true;
      attachListeners(element);
    };

    tryAttach();

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      cleanupRef.current?.();
    };
  }, []);

  return elementRef;
}
